import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FormControl, FormHelperText, FormLabel } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect, useId, useState } from "react";
import { Controller, get, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PickerActionBar } from "./PickerActionBar";
const toDayjs = (value)=>{
    if (!value) return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};
const toIso = (value)=>{
    if (!value?.isValid()) return null;
    return new Date(Date.UTC(value.year(), value.month(), value.date(), 12, 0, 0, 0)).toISOString();
};
export const DateInput = (props)=>{
    const labelId = useId();
    const errorId = useId();
    const formContext = useFormContext();
    const translation = useTranslation();
    const fieldValue = useWatch({
        control: formContext.control,
        name: props.name
    });
    const [value, setValue] = useState(()=>toDayjs(fieldValue));
    const error = get(formContext.formState.errors, props.name);
    const testId = props['data-testid'] ?? props.name;
    useEffect(()=>{
        const externalValue = toDayjs(fieldValue);
        if (!externalValue) {
            if (value) setValue(null);
            return;
        }
        if (toIso(externalValue) !== toIso(value)) {
            setValue(externalValue);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        fieldValue
    ]);
    return /*#__PURE__*/ _jsx(LocalizationProvider, {
        dateAdapter: AdapterDayjs,
        children: /*#__PURE__*/ _jsx(Controller, {
            control: formContext.control,
            name: props.name,
            render: ({ field })=>/*#__PURE__*/ _jsxs(FormControl, {
                    error: !!error,
                    fullWidth: props.fullWidth,
                    required: props.required,
                    size: props.size,
                    children: [
                        /*#__PURE__*/ _jsx(FormLabel, {
                            disabled: props.disabled,
                            id: labelId,
                            required: props.required,
                            sx: props.hideLabel ? visuallyHidden : undefined,
                            children: props.label
                        }),
                        /*#__PURE__*/ _jsx(DatePicker, {
                            disabled: props.disabled,
                            maxDate: props.maxDate,
                            minDate: props.minDate,
                            onChange: (next)=>{
                                setValue(next);
                                /* an incomplete date (e.g. year not fully typed yet) comes
                   through as a non-null invalid Dayjs — leave the committed
                   field value alone so the reset-sync effect below doesn't
                   see a change and wipe out what's still being typed */ if (!next || next.isValid()) {
                                    field.onChange(toIso(next));
                                }
                            },
                            slotProps: {
                                actionBar: {
                                    'data-testid': `clear-${testId}`
                                },
                                textField: {
                                    error: !!error,
                                    fullWidth: props.fullWidth,
                                    inputProps: {
                                        'aria-describedby': errorId,
                                        'aria-labelledby': labelId,
                                        'data-component': 'input',
                                        'data-testid': testId
                                    },
                                    required: props.required,
                                    size: props.size
                                }
                            },
                            slots: {
                                actionBar: PickerActionBar
                            },
                            value: value
                        }),
                        !props.hideError && /*#__PURE__*/ _jsx(FormHelperText, {
                            id: errorId,
                            children: translation.t(error?.message) || ' '
                        })
                    ]
                })
        })
    });
};
DateInput.displayName = 'DateInput';

//# sourceMappingURL=DateInput.js.map